import { createContext, useCallback, useContext, useMemo, useState } from 'react';

const THEME_PREFERENCE_STORAGE_KEY = 'statusreport_theme_preference';
const VALID_THEME_PREFERENCES = new Set(['light', 'dark', 'system']);

const normalizeThemePreference = (value) => {
    if (typeof value !== 'string') {
        return 'light';
    }

    const normalized = value.toLowerCase();
    return VALID_THEME_PREFERENCES.has(normalized) ? normalized : 'light';
};

const getInitialThemePreference = () => {
    try {
        const saved = localStorage.getItem(THEME_PREFERENCE_STORAGE_KEY);
        return normalizeThemePreference(saved || 'light');
    } catch {
        return 'light';
    }
};

const ThemeModeContext = createContext(null);

export const ThemeModeProvider = ({ children }) => {
    const [themePreference, setThemePreferenceState] = useState(getInitialThemePreference);

    const setThemePreference = useCallback((nextPreference) => {
        const normalized = normalizeThemePreference(nextPreference);
        setThemePreferenceState(normalized);

        try {
            localStorage.setItem(THEME_PREFERENCE_STORAGE_KEY, normalized);
        } catch {
            // Ignore storage write failures (private mode, restricted storage, etc.)
        }
    }, []);

    const value = useMemo(
        () => ({
            themePreference,
            setThemePreference,
        }),
        [themePreference, setThemePreference]
    );

    return <ThemeModeContext.Provider value={value}>{children}</ThemeModeContext.Provider>;
};

export const useThemeMode = () => {
    const context = useContext(ThemeModeContext);
    if (!context) {
        throw new Error('useThemeMode must be used within a ThemeModeProvider');
    }
    return context;
};

export default ThemeModeContext;
